import Papa from 'papaparse';
import JSZip from 'jszip';
import saveAs from 'file-saver';
import { WorkItem, GeneratedImage, ReferenceAsset } from '../types';

export const parseCSV = (file: File): Promise<{ data: any[], headers: string[] }> => {
  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      header: false, // Parse as arrays first to let user decide headers
      skipEmptyLines: true,
      complete: (results) => {
        const data = results.data as any[][];
        if (data.length === 0) {
          resolve({ data: [], headers: [] });
          return;
        }
        // Assume first row might be headers
        const headers = data[0].map(h => String(h).trim());
        resolve({ data, headers });
      },
      error: (err) => reject(err),
    });
  });
};

export const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const result = reader.result as string;
      // Remove data URL prefix (data:image/png;base64,)
      const base64 = result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = (error) => reject(error);
  });
};

export const downloadAllImages = async (workItems: WorkItem[]) => {
  const zip = new JSZip();
  let count = 0;

  workItems.forEach((item) => {
    item.generatedImages.forEach((img, imgIdx) => {
      // Structure: {index}_{imgIdx+1}_B{batchStr}.png
      const batchStr = img.batchId ? new Date(img.batchId).toISOString().replace(/[-T:.]/g, '').slice(8, 14) : '000000';
      const fileName = `${item.index}_${imgIdx + 1}_B${batchStr}.png`;
      if (img.base64Data) {
        zip.file(fileName, img.base64Data, { base64: true });
        count++;
      }
    });
  });

  if (count === 0) {
    alert("No images to download.");
    return;
  }

  const content = await zip.generateAsync({ type: 'blob' });
  saveAs(content, `GenStudio_Batch_${new Date().toISOString().slice(0, 10)}.zip`);
};

/**
 * Composites multiple reference assets into a single grid image.
 * Returns the base64 of the new image and a text description of the layout.
 * Adds numeric badges for explicit identification by the model.
 */
export const compositeReferenceImages = async (
  assets: ReferenceAsset[]
): Promise<{ base64: string; description: string }> => {
  if (assets.length === 0) throw new Error("No assets to composite");

  // Limit to 8 images to maintain resolution/clarity
  const safeAssets = assets.slice(0, 8);
  const count = safeAssets.length;

  // Determine Grid Layout
  let cols = 1;
  let rows = 1;
  if (count === 2) { cols = 2; rows = 1; }
  else if (count <= 4) { cols = 2; rows = 2; }
  else if (count <= 6) { cols = 3; rows = 2; }
  else { cols = 3; rows = 3; } // max 8 (9 slots, 1 empty)

  const cellWidth = 512;
  const cellHeight = 512;
  const padding = 20;
  const labelHeight = 40; // Space for text label

  const canvasWidth = (cellWidth * cols) + (padding * (cols + 1));
  const canvasHeight = (cellHeight * rows) + (padding * (rows + 1)) + (labelHeight * rows);

  const canvas = document.createElement('canvas');
  canvas.width = canvasWidth;
  canvas.height = canvasHeight;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error("Could not get canvas context");

  // Fill Background
  ctx.fillStyle = '#0f172a'; // Slate 900 background
  ctx.fillRect(0, 0, canvasWidth, canvasHeight);

  const descriptions: string[] = [];

  // Helper to load image
  const loadImage = (url: string): Promise<HTMLImageElement> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = url;
    });
  };

  for (let i = 0; i < count; i++) {
    const asset = safeAssets[i];
    const col = i % cols;
    const row = Math.floor(i / cols);
    const indexNum = i + 1;

    const x = padding + (col * (cellWidth + padding));
    const y = padding + (row * (cellHeight + labelHeight + padding));

    try {
      const img = await loadImage(asset.url);

      // 1. Draw Image (Cover fit)
      const ratio = Math.max(cellWidth / img.width, cellHeight / img.height);
      const centerShift_x = (cellWidth - img.width * ratio) / 2;
      const centerShift_y = (cellHeight - img.height * ratio) / 2;

      ctx.save();
      ctx.beginPath();
      ctx.rect(x, y + labelHeight, cellWidth, cellHeight);
      ctx.clip();
      ctx.drawImage(
        img,
        0, 0, img.width, img.height,
        x + centerShift_x, y + labelHeight + centerShift_y, img.width * ratio, img.height * ratio
      );
      ctx.restore();

      // 2. Draw Label Bar
      ctx.fillStyle = '#1e293b';
      ctx.fillRect(x, y, cellWidth, labelHeight);

      // 3. Draw Label Text
      ctx.fillStyle = '#e2e8f0';
      ctx.font = 'bold 18px Arial';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(`${asset.type.toUpperCase()}: ${asset.name}`, x + (cellWidth / 2), y + (labelHeight / 2));

      // 4. Draw Numeric Badge (Identification)
      const badgeSize = 24;
      const badgeX = x + 10 + badgeSize;
      const badgeY = y + labelHeight + 10 + badgeSize;

      // Shadow
      ctx.fillStyle = 'rgba(0,0,0,0.5)';
      ctx.beginPath();
      ctx.arc(badgeX + 2, badgeY + 2, badgeSize, 0, Math.PI * 2);
      ctx.fill();

      // Circle
      ctx.fillStyle = '#ef4444'; // Red-500 for high visibility
      ctx.beginPath();
      ctx.arc(badgeX, badgeY, badgeSize, 0, Math.PI * 2);
      ctx.fill();

      // Number
      ctx.fillStyle = '#ffffff';
      ctx.font = 'bold 28px Arial';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(`${indexNum}`, badgeX, badgeY + 2);

      // Add to description
      descriptions.push(`[Image ${indexNum}] is ${asset.name} (Type: ${asset.type}, Keywords: ${asset.keywords || 'none'})`);

    } catch (e) {
      console.error(`Failed to load asset ${asset.name}`, e);
    }
  }

  const base64 = canvas.toDataURL('image/png').split(',')[1];
  const layoutDescription = descriptions.join('; ');

  return { base64, description: layoutDescription };
};